import View from '@play-co/timestep-core/lib/ui/View';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { AssetGroup } from '@play-co/timestep-core';
import { Feature } from 'src/replicant/analytics';
import { downscale } from 'src/conf/scale';
import { SuperLollipopProgress } from '../components/SuperLollipopProgress';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import { clamp } from 'src/replicant/utils';
import i18n from 'src/lib/i18n/i18n';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import buttons from 'src/lib/ui/config/buttons';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import sounds from 'src/lib/sounds';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { SuperLollipopAnimation } from '../components/SuperLollipopAnimation';
import StateObserver from 'src/StateObserver';
import { spineLoader } from '@play-co/timestep-spine';
import uiConfig from 'src/lib/ui/config';

type CloseHandler = (ok: boolean) => void;

export default class PopupSuperLollipopProgress extends PopupBasicLazy {
  private titleImage: ImageScaleView;
  private progressBar: SuperLollipopProgress;
  private cta: ButtonScaleViewWithText;
  private animation: SuperLollipopAnimation;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      boxOpts: {
        image: 'assets/ui/superLollipop/popupProgress/popup.png',
      },
      skipTitle: true,
      width: downscale(1080),
      height: downscale(1620),
      close: () => {
        opts.close(false);
      },
      tracking: {
        feature: Feature.SUPER_LOLLIPOP._,
        $subFeature: Feature.SUPER_LOLLIPOP.POPUP_PROGRESS,
      },
      zIndex: 10000,
    });

    const buttonY = 110;
    const buttonXOffset = 8;

    this.buttonClose.updateOpts({
      x:
        this.box.style.width - this.buttonClose.style.width / 2 - buttonXOffset,
      y: buttonY,
    });

    this.animation = new SuperLollipopAnimation({
      superview: this.box,
      centerOnOrigin: true,
      canHandleEvents: false,
      x: this.box.style.width * 0.5,
      y: 325,
    });

    // Info button
    new ButtonScaleView({
      superview: this.box,
      x: uiConfig.buttons.info.width / 2 + buttonXOffset,
      y: buttonY,
      ...uiConfig.buttons.info,
      tracking: {
        feature: Feature.SUPER_LOLLIPOP._,
        $subFeature: Feature.SUPER_LOLLIPOP.POPUP_PROGRESS_INFO,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        openPopupPromise('popupSuperLollipopInfo', {});
      },
    });

    this.titleImage = new ImageScaleView({
      superview: this.box,
      scaleMethod: 'contain',
      x: this.box.style.width * 0.5,
      y: 85,
      centerOnOrigin: true,
    });

    this.progressBar = new SuperLollipopProgress({
      superview: this.box,
      x: this.box.style.width * 0.5,
      y: 700,
      centerOnOrigin: true,
    });

    this.message.updateOpts({
      color: '#9C693A',
      y: 825,
      size: 30,
    });

    this.cta = new ButtonScaleViewWithText({
      superview: this.box,
      ...buttons.secondary,
      x: this.box.style.width * 0.5,
      y: 990,
      fontSize: 36,
      labelOffsetY: -2,
      font: 'Body',
      width: 300,
      height: 80,
      centerOnOrigin: true,
      onClick: async () => {
        sounds.sfx.play('ok');
        opts.close(true);
      },
      tracking: {
        feature: Feature.SUPER_LOLLIPOP._,
        $subFeature: Feature.SUPER_LOLLIPOP.POPUP_PROGRESS_CTA,
      },
    });
  }

  public getAssetsGroup() {
    return new AssetGroup([
      ...AssetGroup.constructURLs(['assets/ui/superLollipop/shared']),
      ...AssetGroup.constructURLs(['assets/ui/superLollipop/popupProgress']),
      {
        loadMethod: spineLoader,
        url: 'assets/ui/superLollipop/animation/shine-and-confetti',
      },
    ]);
  }

  public async init(args: { currentStreak: number; maxStreak: number }) {
    super.init({});
    const maxStreak = args.maxStreak;
    const currentStreak = clamp(args.currentStreak, 0, maxStreak);
    const remaining = maxStreak - currentStreak;
    this.progressBar.setProgress(currentStreak);

    if (remaining > 1) {
      // More than one remaining
      this.message.localeText = () =>
        i18n('superLollipop.popupProgress.messagePlural', { remaining });
    } else if (remaining === 1) {
      // Exactly one remaining
      this.message.localeText = () =>
        i18n('superLollipop.popupProgress.messageSingular', { remaining });
    } else {
      // No remaining - super lollipop is active
      this.message.localeText = () =>
        i18n('superLollipop.popupProgress.messageActive');
    }

    if (currentStreak === 0) {
      this.cta.localeText = () => i18n('superLollipop.popupProgress.ctaStart');
    } else {
      this.cta.localeText = () => i18n('superLollipop.popupProgress.ctaPlay');
    }

    const locale = StateObserver.getLocale();

    this.titleImage.updateOpts({
      image: `assets/ui/superLollipop/popupProgress/title_${locale}.png`,
      width: downscale(locale === 'ja' ? 540 : 607),
      height: downscale(locale === 'ja' ? 148 : 113),
      centerOnOrigin: true,
    });
  }

  public async fadeIn() {
    await super.fadeIn();
    sounds.playSound('puzzle-win-confetti', 0.5);
  }
}
